import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getSmartFocusCardsForUser } from '../services/intelligence/focusEngine';

/**
 * Unified card schema for Ask Swirl landing cards
 */
export const cardSchema = {
  id: '',
  type: '', // "active-focus" | "focus-suggestion" | "insight"
  title: '',
  description: '',
  ctaLabel: '',
  ctaAction: () => {},
  gradient: '',
  icon: null,
};

/**
 * Hook to return landing cards for Ask Swirl landing page
 * Uses intelligence engine to generate cards based on user data
 * Supports realtime refresh on user activity changes
 */
export function useSmartCards() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs for debouncing and subscription management
  const debounceTimeoutRef = useRef(null);
  const subscriptionsRef = useRef([]);

  const isInitialLoadRef = useRef(true);

  const loadCards = useCallback(async (skipIfRunning = false) => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    console.log('[useSmartCards] loading cards for user', user?.id);
    
    // Only set loading state on initial load, not on realtime refreshes
    // This prevents visual flicker during updates
    const isInitialLoad = isInitialLoadRef.current;
    if (isInitialLoad) {
      setLoading(true);
      isInitialLoadRef.current = false;
    }
    setError(null);

    try {
      const focusCards = await getSmartFocusCardsForUser(user.id, navigate);
      
      // Avoid clearing cards when engine is still warming up
      if (focusCards === null) {
        console.log('[useSmartCards] Engine still running, keeping existing cards');
        return;
      }
      
      // Only update when focusCards is a valid array
      if (Array.isArray(focusCards)) {
        console.log('[useSmartCards] loaded cards', focusCards);
        setCards(focusCards);
        return;
      }
      
      // Fail safe for unexpected payload
      console.log('[useSmartCards] Unexpected card payload, failing safe with []');
      setCards([]);
    } catch (err) {
      console.error('[useSmartCards] error loading cards', err);
      setError(err.message || 'Failed to load focus cards');
      // Set empty array on error - AskSwirlEmptyState will show empty state
      setCards([]);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
      console.log('[useSmartCards] loading complete');
    }
  }, [user?.id, navigate]);

  // Debounced refresh function
  const debouncedRefresh = useCallback((triggerSource) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      console.log(`[useSmartCards] realtime refresh triggered by: ${triggerSource}`);
      loadCards(true); // Pass skipIfRunning flag for realtime refreshes
    }, 300);
  }, [loadCards]);

  // Initial load on mount or user change
  useEffect(() => {
    if (user?.id) {
      loadCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Only depend on user.id, not loadCards to avoid re-renders

  // Set up realtime subscriptions
  useEffect(() => {
    if (!user?.id) return;

    console.log('[useSmartCards] Setting up realtime subscriptions for user', user.id);

    const subscriptions = [];

    // Subscribe to instagram_content INSERT
    const instagramInsertChannel = supabase
      .channel(`smart-cards-instagram-insert-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'instagram_content',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        debouncedRefresh('instagram_content:INSERT');
      })
      .subscribe();
    subscriptions.push(instagramInsertChannel);

    // Subscribe to instagram_content DELETE
    const instagramDeleteChannel = supabase
      .channel(`smart-cards-instagram-delete-${user.id}`)
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'instagram_content',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        debouncedRefresh('instagram_content:DELETE');
      })
      .subscribe();
    subscriptions.push(instagramDeleteChannel);

    // Subscribe to deepsearch.items INSERT
    const deepsearchInsertChannel = supabase
      .channel(`smart-cards-deepsearch-insert-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'deepsearch',
        table: 'items',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        debouncedRefresh('deepsearch.items:INSERT');
      })
      .subscribe();
    subscriptions.push(deepsearchInsertChannel);

    // Subscribe to deepsearch.items DELETE
    const deepsearchDeleteChannel = supabase
      .channel(`smart-cards-deepsearch-delete-${user.id}`)
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'deepsearch',
        table: 'items',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        debouncedRefresh('deepsearch.items:DELETE');
      })
      .subscribe();
    subscriptions.push(deepsearchDeleteChannel);

    // Subscribe to chat_messages INSERT (only user messages)
    const chatMessagesInsertChannel = supabase
      .channel(`smart-cards-chat-messages-insert-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        // Only trigger on user messages, not assistant messages
        if (payload.new.role === 'user') {
          debouncedRefresh('chat_messages:INSERT');
        }
      })
      .subscribe();
    subscriptions.push(chatMessagesInsertChannel);

    // Subscribe to conversations INSERT
    const conversationsInsertChannel = supabase
      .channel(`smart-cards-conversations-insert-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'conversations',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        debouncedRefresh('conversations:INSERT');
      })
      .subscribe();
    subscriptions.push(conversationsInsertChannel);

    // Subscribe to conversations UPDATE (title or updated_at changes)
    const conversationsUpdateChannel = supabase
      .channel(`smart-cards-conversations-update-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'conversations',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        // Only trigger if title or updated_at changed
        if (payload.new.title !== payload.old.title || 
            payload.new.updated_at !== payload.old.updated_at) {
          debouncedRefresh('conversations:UPDATE');
        }
      })
      .subscribe();
    subscriptions.push(conversationsUpdateChannel);

    // Subscribe to folders INSERT
    const foldersInsertChannel = supabase
      .channel(`smart-cards-folders-insert-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'folders',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        debouncedRefresh('folders:INSERT');
      })
      .subscribe();
    subscriptions.push(foldersInsertChannel);

    // Subscribe to folders UPDATE
    const foldersUpdateChannel = supabase
      .channel(`smart-cards-folders-update-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'folders',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        debouncedRefresh('folders:UPDATE');
      })
      .subscribe();
    subscriptions.push(foldersUpdateChannel);

    // Subscribe to pinned_moments INSERT
    const pinnedMomentsInsertChannel = supabase
      .channel(`smart-cards-pinned-moments-insert-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'pinned_moments',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        debouncedRefresh('pinned_moments:INSERT');
      })
      .subscribe();
    subscriptions.push(pinnedMomentsInsertChannel);

    // Subscribe to pinned_moments DELETE
    const pinnedMomentsDeleteChannel = supabase
      .channel(`smart-cards-pinned-moments-delete-${user.id}`)
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'pinned_moments',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        debouncedRefresh('pinned_moments:DELETE');
      })
      .subscribe();
    subscriptions.push(pinnedMomentsDeleteChannel);

    // Store subscriptions for cleanup
    subscriptionsRef.current = subscriptions;

    // Cleanup function
    return () => {
      console.log('[useSmartCards] Cleaning up realtime subscriptions');
      // Clear debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      // Unsubscribe from all channels
      subscriptions.forEach(channel => {
        channel.unsubscribe();
      });
      subscriptionsRef.current = [];
    };
  }, [user?.id, debouncedRefresh]);

  return { 
    cards, 
    loading, 
    error,
    refresh: loadCards 
  };
}
